import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Palette, Plus, Pencil, Trash, Check, Copy } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const predefinedSwatches = [
  {
    name: 'Forest',
    colors: {
      background: '#FDFBF7',
      primary: '#1E3A2B',
      accent: '#D95D39',
      text_primary: '#1A1918'
    }
  },
  {
    name: 'Ocean',
    colors: {
      background: '#F0F9FF',
      primary: '#0369A1',
      accent: '#F97316',
      text_primary: '#0F172A'
    }
  },
  {
    name: 'Sunset',
    colors: {
      background: '#FFFBEB',
      primary: '#B45309',
      accent: '#DC2626',
      text_primary: '#1C1917'
    }
  },
  {
    name: 'Midnight',
    colors: {
      background: '#F8FAFC',
      primary: '#1E293B',
      accent: '#6366F1',
      text_primary: '#0F172A'
    }
  },
  {
    name: 'Rose',
    colors: {
      background: '#FFF1F2',
      primary: '#9F1239',
      accent: '#BE185D',
      text_primary: '#1C1917',
      card: '#FFFFFF',
      border: '#FECDD3',
      success: '#15803D',
      warning: '#D97706',
      error: '#DC2626',
      info: '#7C3AED'
    }
  },
  {
    name: 'Slate',
    colors: {
      background: '#F1F5F9',
      primary: '#334155',
      accent: '#0EA5E9',
      text_primary: '#0F172A',
      card: '#FFFFFF',
      border: '#CBD5E1',
      success: '#16A34A',
      warning: '#EAB308',
      error: '#EF4444',
      info: '#3B82F6'
    }
  },
  {
    name: 'Emerald',
    colors: {
      background: '#ECFDF5',
      primary: '#065F46',
      accent: '#D97706',
      text_primary: '#022C22',
      card: '#FFFFFF',
      border: '#A7F3D0',
      success: '#059669',
      warning: '#F59E0B',
      error: '#DC2626',
      info: '#0284C7'
    }
  },
  {
    name: 'Lavender',
    colors: {
      background: '#FAF5FF',
      primary: '#6D28D9',
      accent: '#EC4899',
      text_primary: '#1E1B4B',
      card: '#FFFFFF',
      border: '#DDD6FE',
      success: '#059669',
      warning: '#F59E0B',
      error: '#E11D48',
      info: '#2563EB'
    }
  },
  {
    name: 'Charcoal',
    colors: {
      background: '#FAFAFA',
      primary: '#18181B',
      accent: '#EF4444',
      text_primary: '#09090B',
      card: '#FFFFFF',
      border: '#E4E4E7',
      success: '#22C55E',
      warning: '#F59E0B',
      error: '#DC2626',
      info: '#3B82F6'
    }
  },
  {
    name: 'Terracotta',
    colors: {
      background: '#FEF7EE',
      primary: '#92400E',
      accent: '#B91C1C',
      text_primary: '#422006',
      card: '#FFFBF5',
      border: '#FDE68A',
      success: '#15803D',
      warning: '#CA8A04',
      error: '#DC2626',
      info: '#1D4ED8'
    }
  }
];

const ThemesPage = () => {
  const { fetchSettings } = useAuth();
  const [themes, setThemes] = useState([]);
  const [appSettings, setAppSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    colors: {
      background: '#FDFBF7',
      card: '#FFFFFF',
      subtle: '#F5F3E9',
      primary: '#1E3A2B',
      primary_hover: '#162D21',
      primary_foreground: '#FFFFFF',
      accent: '#D95D39',
      accent_hover: '#BE4F2E',
      accent_foreground: '#FFFFFF',
      text_primary: '#1A1918',
      text_secondary: '#5C5A56',
      text_muted: '#8A8782',
      border: '#E5E2D9',
      success: '#2D6A4F',
      warning: '#F4A261',
      error: '#E63946',
      info: '#457B9D'
    },
    is_default: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [themesRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/themes`),
        axios.get(`${API_URL}/api/settings/app`)
      ]);
      setThemes(themesRes.data);
      setAppSettings(settingsRes.data);
    } catch (e) {
      toast.error('Failed to fetch themes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTheme) {
        await axios.put(`${API_URL}/api/themes/${editingTheme.id}`, formData);
        toast.success('Theme updated');
      } else {
        await axios.post(`${API_URL}/api/themes`, formData);
        toast.success('Theme created');
      }
      fetchData();
      closeDialog();
    } catch (e) {
      toast.error('Failed to save theme');
    }
  };

  const handleDelete = async (themeId) => {
    if (!window.confirm('Delete this theme?')) return;
    try {
      await axios.delete(`${API_URL}/api/themes/${themeId}`);
      toast.success('Theme deleted');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete theme');
    }
  };

  const handleSetActive = async (themeId) => {
    try {
      await axios.put(`${API_URL}/api/settings/app`, { active_theme_id: themeId });
      toast.success('Theme activated');
      fetchData();
      fetchSettings();
    } catch (e) {
      toast.error('Failed to activate theme');
    }
  };

  const applySwatch = (swatch) => {
    setFormData(prev => ({
      ...prev,
      colors: { ...prev.colors, ...swatch.colors }
    }));
    toast.success(`Applied ${swatch.name} swatch`);
  };

  const duplicateTheme = (theme) => {
    setEditingTheme(null);
    setFormData({
      name: `${theme.name} (Copy)`,
      colors: { ...theme.colors },
      is_default: false
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (theme) => {
    setEditingTheme(theme);
    setFormData({
      name: theme.name,
      colors: theme.colors,
      is_default: theme.is_default
    });
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingTheme(null);
    setFormData({
      name: '',
      colors: {
        background: '#FDFBF7',
        card: '#FFFFFF',
        subtle: '#F5F3E9',
        primary: '#1E3A2B',
        primary_hover: '#162D21',
        primary_foreground: '#FFFFFF',
        accent: '#D95D39',
        accent_hover: '#BE4F2E',
        accent_foreground: '#FFFFFF',
        text_primary: '#1A1918',
        text_secondary: '#5C5A56',
        text_muted: '#8A8782',
        border: '#E5E2D9',
        success: '#2D6A4F',
        warning: '#F4A261',
        error: '#E63946',
        info: '#457B9D'
      },
      is_default: false
    });
  };

  const updateColor = (key, value) => {
    setFormData(prev => ({
      ...prev,
      colors: { ...prev.colors, [key]: value }
    }));
  };

  if (loading) {
    return <div className="h-96 skeleton rounded-md"></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Themes</h1>
          <p className="text-muted-foreground">Customize application colors</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => closeDialog()} data-testid="add-theme-btn">
              <Plus size={18} className="mr-2" />
              Add Theme
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingTheme ? 'Edit Theme' : 'Create Theme'}</DialogTitle>
              <DialogDescription>Customize the color palette</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label>Theme Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="My Custom Theme"
                  required
                  data-testid="theme-name"
                />
              </div>

              <div className="space-y-2">
                <Label>Quick Swatches</Label>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  {predefinedSwatches.map(swatch => (
                    <button
                      key={swatch.name}
                      type="button"
                      onClick={() => applySwatch(swatch)}
                      className="group flex flex-col items-center gap-1.5 p-2 rounded-lg border hover:border-primary/50 hover:bg-secondary/50 transition-all"
                      data-testid={`swatch-${swatch.name.toLowerCase()}`}
                    >
                      <div className="flex gap-0.5">
                        <div className="w-5 h-5 rounded-sm border shadow-sm" style={{ backgroundColor: swatch.colors.background }} />
                        <div className="w-5 h-5 rounded-sm border shadow-sm" style={{ backgroundColor: swatch.colors.primary }} />
                        <div className="w-5 h-5 rounded-sm border shadow-sm" style={{ backgroundColor: swatch.colors.accent }} />
                        <div className="w-5 h-5 rounded-sm border shadow-sm" style={{ backgroundColor: swatch.colors.text_primary }} />
                      </div>
                      <span className="text-xs text-muted-foreground group-hover:text-foreground">{swatch.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Live Preview */}
              <div className="space-y-2">
                <Label>Preview</Label>
                <div className="rounded-lg border overflow-hidden" style={{ backgroundColor: formData.colors.background }}>
                  <div className="px-4 py-2 flex items-center gap-2" style={{ backgroundColor: formData.colors.primary }}>
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: formData.colors.primary_foreground || '#FFFFFF', opacity: 0.8 }} />
                    <span className="text-xs font-medium" style={{ color: formData.colors.primary_foreground || '#FFFFFF' }}>Header Bar</span>
                  </div>
                  <div className="p-4 space-y-2">
                    <p className="text-sm font-semibold" style={{ color: formData.colors.text_primary }}>Sample Dashboard</p>
                    <p className="text-xs" style={{ color: formData.colors.text_secondary || formData.colors.text_muted }}>Secondary descriptive text</p>
                    <div className="flex gap-2 pt-1">
                      <span className="px-2 py-0.5 rounded text-xs text-white" style={{ backgroundColor: formData.colors.primary }}>Primary</span>
                      <span className="px-2 py-0.5 rounded text-xs text-white" style={{ backgroundColor: formData.colors.accent }}>Accent</span>
                      <span className="px-2 py-0.5 rounded text-xs text-white" style={{ backgroundColor: formData.colors.success }}>Success</span>
                      <span className="px-2 py-0.5 rounded text-xs text-white" style={{ backgroundColor: formData.colors.error }}>Error</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <Label>Color Palette</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {Object.entries(formData.colors).map(([key, value]) => (
                    <div key={key} className="space-y-1">
                      <Label className="text-xs text-muted-foreground capitalize">
                        {key.replace(/_/g, ' ')}
                      </Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={value}
                          onChange={(e) => updateColor(key, e.target.value)}
                          className="w-8 h-8 rounded cursor-pointer"
                        />
                        <Input
                          value={value}
                          onChange={(e) => updateColor(key, e.target.value)}
                          className="h-8 text-xs font-mono"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={closeDialog}>Cancel</Button>
                <Button type="submit" data-testid="save-theme-btn">
                  {editingTheme ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {themes.map(theme => (
          <Card key={theme.id} className={`card-hover ${appSettings.active_theme_id === theme.id ? 'ring-2 ring-primary' : ''}`}>
            <CardHeader className="flex flex-row items-start justify-between pb-2">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Palette size={20} className="text-primary" />
                  {theme.name}
                  {theme.is_default && <Badge variant="secondary">Default</Badge>}
                  {appSettings.active_theme_id === theme.id && <Badge variant="default">Active</Badge>}
                </CardTitle>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => duplicateTheme(theme)} title="Duplicate theme" data-testid={`copy-theme-${theme.id}`}>
                  <Copy size={16} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => openEditDialog(theme)} data-testid={`edit-theme-${theme.id}`}>
                  <Pencil size={16} />
                </Button>
                {!theme.is_default && (
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(theme.id)} className="text-destructive" data-testid={`delete-theme-${theme.id}`}>
                    <Trash size={16} />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-4">
                {['background', 'primary', 'accent', 'success', 'warning', 'error'].map(key => (
                  <div
                    key={key}
                    className="w-8 h-8 rounded-md border"
                    style={{ backgroundColor: theme.colors?.[key] }}
                    title={key}
                  />
                ))}
              </div>
              {appSettings.active_theme_id !== theme.id && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSetActive(theme.id)}
                  data-testid={`activate-theme-${theme.id}`}
                >
                  <Check size={16} className="mr-1" /> Activate
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ThemesPage;
